import React, { useEffect, useState, useRef } from 'react';
import { 
    Card, 
    Input, 
    Button, 
    List, 
    Avatar, 
    Typography, 
    Space, 
    Badge, 
    Tooltip,
    message,
    Spin,
    Empty
} from 'antd';
import { 
    SendOutlined, 
    UserOutlined, 
    MessageOutlined,
    CheckOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import hybridChatService from '../../services/hybridChat';

const { TextArea } = Input;
const { Text } = Typography;

function ChatInterface({ leadId, onClose, isVendor = false }) {
    const { user } = useAuth();
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [otherUserTyping, setOtherUserTyping] = useState(false);
    const messagesEndRef = useRef(null);
    const typingTimeoutRef = useRef(null);

    useEffect(() => {
        if (leadId) {
            fetchMessages();
            initializeChatService();
            // Mark messages as read when chat is opened
            markMessagesAsRead();
        }

        return () => {
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
            hybridChatService.disconnect();
        };
    }, [leadId]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const initializeChatService = async () => {
        try {
            await hybridChatService.initialize(leadId);
            
            // Listen for new messages
            hybridChatService.onNewMessage((messages) => {
                setMessages(prev => [...prev, ...messages]);
            });

            // Listen for typing indicators
            hybridChatService.onTyping((data) => {
                if (data.user_id !== user.id) {
                    setOtherUserTyping(data.is_typing);
                    if (data.is_typing) {
                        setTimeout(() => setOtherUserTyping(false), 2000);
                    }
                }
            });

            console.log(`Chat service initialized using: ${hybridChatService.getServiceType()}`);
        } catch (error) {
            console.error('Failed to initialize chat service:', error);
        }
    };

    const fetchMessages = async () => {
        try {
            setLoading(true);
            const response = await api.getLead(leadId);
            if (response.success) {
                setMessages(response.data.messages || []);
            }
        } catch (error) {
            message.error('Failed to fetch messages');
        } finally {
            setLoading(false);
        }
    };

    const markMessagesAsRead = async () => {
        try {
            await api.markLeadMessagesAsRead(leadId);
        } catch (error) {
            console.error('Failed to mark messages as read:', error);
        }
    };

    const handleSendMessage = async () => {
        if (!newMessage.trim() || sending) return;

        try {
            setSending(true);
            const response = await api.sendLeadMessage(leadId, {
                message: newMessage.trim()
            });

            if (response.success) {
                setMessages(prev => [...prev, response.data]);
                setNewMessage('');
                
                // Stop typing indicator
                hybridChatService.sendTyping(false);
            }
        } catch (error) {
            message.error('Failed to send message');
        } finally {
            setSending(false);
        }
    };

    const handleTyping = (e) => {
        setNewMessage(e.target.value);

        hybridChatService.sendTyping(true);
        
        // Clear existing timeout
        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }
        
        // Set new timeout to stop typing
        typingTimeoutRef.current = setTimeout(() => {
            hybridChatService.sendTyping(false);
        }, 1000);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffInHours = (now - date) / (1000 * 60 * 60);

        if (diffInHours < 24) {
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } else {
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }
    };

    const isMyMessage = (message) => {
        return message.user_id === user.id;
    };

    const getMessageStatus = (message) => {
        if (message.is_read) {
            return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
        }
        return <CheckOutlined style={{ color: '#8c8c8c' }} />;
    };

    if (loading) {
        return (
            <Card className="h-96 flex items-center justify-center">
                <Spin size="large" />
            </Card>
        );
    }

    return (
        <Card 
            title={
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <MessageOutlined />
                        <span>Chat</span>
                        {otherUserTyping && (
                            <Badge status="processing" text="Typing..." />
                        )}
                    </div>
                    {onClose && (
                        <Button type="text" onClick={onClose}>
                            ×
                        </Button>
                    )}
                </div>
            }
            className="h-96 flex flex-col"
            bodyStyle={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto mb-4 pr-2">
                {messages.length === 0 ? (
                    <Empty 
                        description="No messages yet" 
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                ) : (
                    <List
                        dataSource={messages}
                        renderItem={(msg) => (
                            <List.Item className="!block !p-0 mb-3">
                                <div className={`flex ${isMyMessage(msg) ? 'justify-end' : 'justify-start'}`}>
                                    <div className={`max-w-xs lg:max-w-md ${isMyMessage(msg) ? 'order-2' : 'order-1'}`}>
                                        <div className={`flex items-start gap-2 ${isMyMessage(msg) ? 'flex-row-reverse' : 'flex-row'}`}>
                                            <Avatar 
                                                size="small" 
                                                icon={<UserOutlined />}
                                                className={isMyMessage(msg) ? 'ml-2' : 'mr-2'}
                                            />
                                            <div className={`rounded-lg px-3 py-2 ${
                                                isMyMessage(msg) 
                                                    ? 'bg-blue-500 text-white' 
                                                    : 'bg-gray-100 text-gray-800'
                                            }`}>
                                                <div className="text-sm">{msg.message}</div>
                                                <div className={`text-xs mt-1 flex items-center gap-1 ${
                                                    isMyMessage(msg) ? 'text-blue-100' : 'text-gray-500'
                                                }`}>
                                                    <span>{formatTime(msg.created_at)}</span>
                                                    {isMyMessage(msg) && getMessageStatus(msg)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </List.Item>
                        )}
                    />
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Typing Indicator */}
            {otherUserTyping && (
                <div className="mb-2 text-sm text-gray-500 italic">
                    Someone is typing...
                </div>
            )}

            {/* Message Input */}
            <div className="flex gap-2">
                <TextArea
                    value={newMessage}
                    onChange={handleTyping}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your message..."
                    autoSize={{ minRows: 1, maxRows: 3 }}
                    className="flex-1"
                    disabled={sending}
                />
                <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={handleSendMessage}
                    loading={sending}
                    disabled={!newMessage.trim()}
                >
                    Send
                </Button>
            </div>
        </Card>
    );
}

export default ChatInterface;
